import React, { useEffect, useState } from 'react';
import { Table, message, Button, Space, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import config from '../utils/config';
import axiosInstance from '../utils/axiosInstance';
import Heading from "../components/Heading";
import {
  BarChartOutlined,
  ReloadOutlined,
  EditOutlined,
  EyeOutlined,
} from '@ant-design/icons';

interface Result {
  id: string;
  constituency_name: string;
  totalVoters: number;
  npp: number;
  ndc: number;
  others: number;
  totalVoteCount: number;
}

const ResultsList: React.FC = () => {
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('presidential'); // Track active tab
  const navigate = useNavigate();

  useEffect(() => {
    fetchResults(); // eslint-disable-next-line
  }, [activeTab]);

  // Fetch results from the API
  const fetchResults = async () => {
    setLoading(true);
    try {
      // Dynamically use the activeTab for election_type
      const electionType = activeTab; // 'presidential' or 'parliamentary'
      const response = await axiosInstance.get(
          `${config.api.endpoints.results_by_election_type}/?election_type=${electionType}`, // Add trailing slash
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`, // Ensure token is correctly fetched
            },
          }
      );

      // Map results to match the data structure
      setResults(
          response.data.map((item: any) => ({
            id: item.id,
            constituency_name: item.constituency_name,
            totalVoters: item.total_vote_cast,
            npp: item.party_votes.find((p: any) => p.party_id === 1)?.votes || 0,
            ndc: item.party_votes.find((p: any) => p.party_id === 2)?.votes || 0,
            others: item.party_votes
                .filter((p: any) => ![1, 2].includes(p.party_id))
                .reduce((sum: number, p: any) => sum + p.votes, 0),
            totalVoteCount: item.total_vote_cast,
          }))
      );
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Handle no results found case
        message.info(`No results found for ${activeTab} elections.`); // Use activeTab here
        setResults([]); // Set an empty array for results
      } else {
        console.error('Error fetching results:', error);
        const errorMessage =
            error.response?.data?.detail || 'Failed to fetch results';
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Constituency Name',
      dataIndex: 'constituency_name',
      key: 'constituency_name',
    },
    {
      title: 'Total Voters',
      dataIndex: 'totalVoters',
      key: 'totalVoters',
    },
    {
      title: 'NPP',
      dataIndex: 'npp',
      key: 'npp',
    },
    {
      title: 'NDC',
      dataIndex: 'ndc',
      key: 'ndc',
    },
    {
      title: 'Others',
      dataIndex: 'others',
      key: 'others',
    },
    {
      title: 'Total Vote Count',
      dataIndex: 'totalVoteCount',
      key: 'totalVoteCount',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Result) => (
          <Space size="middle">
            <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/results/${record.id}`)}
            />
            <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => navigate(`/results/edit/${record.id}`)}
            />
            <Button
                type="link"
                icon={<ReloadOutlined />}
                onClick={() => console.log(`Refreshing result for ${record.constituency_name}`)}
            />
          </Space>
      ),
    },
  ];

  return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <BarChartOutlined className="text-2xl text-red-600" />
            <Heading>Election Results</Heading>
          </div>
          <div className="flex items-center gap-4">
            <Button
                type={activeTab === 'presidential' ? 'primary' : 'default'}
                onClick={() => setActiveTab('presidential')}
            >
              Presidential
            </Button>
            <Button
                type={activeTab === 'parliamentary' ? 'primary' : 'default'}
                onClick={() => setActiveTab('parliamentary')}
            >
              Parliamentary
            </Button>
            <Button type="default" onClick={() => navigate('/settings')}>
              Back to Settings
            </Button>
          </div>
        </div>

        {results.length > 0 ? (
            <Table
                dataSource={results}
                columns={columns}
                rowKey="id"
                loading={loading}
            />
        ) : (
            <Empty
                description={`No results found for ${activeTab} elections`}
                style={{ marginTop: 50 }}
            />
        )}
      </div>
  );
};

export default ResultsList;